import React, { useState } from "react";
import { View, StyleSheet } from "react-native";

import CustomText from "../../../common/general/CustomText/CustomText";
import CustomButton from "../../../common/general/CustomButton/CustomButton";
import TextEditor from "../../../TextEditor/TextEditor";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";
import CustomHtml from "../../../common/general/CustomHtml/CustomHtml";

interface DiscussionHeaderProps {
  title: string;
  author: string;
  numberOfComments: number;
  message: string;
  handleResponse: (response: string) => void;
  currentUser?: {
    id: string;
    name: string;
  };
}

const DiscussionHeader: React.FC<DiscussionHeaderProps> = ({
  title,
  author,
  numberOfComments,
  message,
  handleResponse,
  currentUser,
}) => {
  const [responseContent, setResponseContent] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getStudentInitial = (name: string) => {
    return name.charAt(0).toUpperCase();
  };

  const handleSubmitResponse = async () => {
    if (!responseContent.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await handleResponse(responseContent);
      setResponseContent("");
    } catch (error) {
      console.error("Error submitting response:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <View style={styles.container}>
      {/* Main Discussion Post */}
      <View style={styles.discussionPost}>
        <View style={styles.header}>
          <View style={styles.authorAvatar}>
            <CustomText
              text={getStudentInitial(author)}
              size="s"
              weight="bold"
              style={styles.avatarText}
              useTranslationText={false}
            />
          </View>
          <View style={styles.headerInfo}>
            <CustomText text={title} size="l" weight="bold" style={styles.title} useTranslationText={false} />
            <View style={styles.authorRow}>
              <CustomText text={`by ${author}`} size="s" style={styles.authorName} useTranslationText={false} />
              <CustomText
                text={`${numberOfComments} ${numberOfComments === 1 ? "comment" : "comments"}`}
                size="s"
                style={styles.commentCountText}
                useTranslationText={false}
              />
            </View>
          </View>
        </View>

        <CustomSpacing size="m" type="vertical" />

        <View style={styles.messageContainer}>
          <CustomHtml html={message} textStyle={styles.message} />
        </View>
      </View>

      <CustomSpacing size="xl" type="vertical" />

      {/* Comment Count Display */}
      <CustomText
        text={`${numberOfComments} ${numberOfComments === 1 ? "comment" : "comments"}`}
        size="l"
        weight="medium"
        style={styles.commentCountDisplay}
        useTranslationText={false}
      />

      <CustomSpacing size="m" type="vertical" />

      {/* Response Section */}
      <View style={styles.responseContainer}>
        <View style={styles.replyAvatarsContainer}>
          <View style={styles.replyAvatar}>
            <CustomText
              text={currentUser ? getStudentInitial(currentUser.name) : "U"}
              size="xs"
              weight="bold"
              style={styles.avatarText}
              useTranslationText={false}
            />
          </View>
          <CustomText text="Add a comment" size="s" weight="bold" style={styles.postCommentText} />
        </View>
        <CustomSpacing size="xl" type="vertical" />
        <View style={styles.editorContainer}>
          <TextEditor
            value={responseContent}
            onChange={setResponseContent}
            placeholder="Write your response here..."
            disabled={isSubmitting}
          />
        </View>

        <CustomSpacing size="m" type="vertical" />

        <View style={styles.buttonContainer}>
          <CustomButton
            text="Post Comment"
            onPress={handleSubmitResponse}
            styleType="primary"
            size="m"
            disabled={!responseContent.trim() || isSubmitting}
            useTranslationText={false}
            bold={true}
          />
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
  },
  discussionPost: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  header: {
    flexDirection: "row",
    alignItems: "flex-start",
    borderBottomWidth: 0.553,
    borderBottomColor: "#E8E8E8",
    paddingHorizontal: 32,
    paddingVertical: 24,
  },
  authorAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: "#3E68FE",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 16,
  },
  avatarText: {
    color: "#FFFFFF",
    fontSize: 16,
  },
  headerInfo: {
    flex: 1,
  },
  title: {
    color: "#101828",
    marginBottom: 4,
  },
  authorRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  authorName: {
    color: "#6B7280",
  },
  commentCountText: {
    color: "#6B7280",
  },
  messageContainer: {
    width: "100%",
    paddingHorizontal: 32,
    paddingBottom: 24,
  },
  message: {
    color: "#374151",
    lineHeight: 24,
  },
  commentCountDisplay: {
    color: "#101828",
  },
  postCommentText: {
    color: "#161616",
  },
  responseContainer: {
    backgroundColor: "#F9FAFB",
    borderRadius: 6,
    paddingHorizontal: 32,
    paddingVertical: 24,
  },
  editorContainer: {
    minHeight: 120,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    overflow: "hidden",
    backgroundColor: "#FFFFFF",
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
  },
  replyAvatarsContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 7,
  },
  replyAvatar: {
    width: 44,
    height: 44,
    borderRadius: 360,
    backgroundColor: "#3E68FE",
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 2,
    borderColor: "#FFFFFF",
  },
});

export default DiscussionHeader;
